import React from 'react';

import { Alert } from 'ml-rubiks-styleguide/src/alert';
import { FlexBox } from 'ml-rubiks-styleguide/src/box';
import { Button } from 'ml-rubiks-styleguide/src/button';
import { Typography } from 'ml-rubiks-styleguide/src/typography';

import { useI18n } from '../../contexts/i18n-context';
import { PaymentError } from '../../core/payment-error';

type AlertSeverity = 'error' | 'warning';

interface UnifiedErrorProps {
    message: string;
    severity?: AlertSeverity;
    dismissible?: boolean;
    onDismiss?: () => void;
    onRetry?: () => void;
    retrying?: boolean;
    error?: PaymentError | Error;
}

function getErrorMessage(
    error: PaymentError | Error,
    t: (key: string, fallback: string) => string
): string {
    if (error instanceof PaymentError && error.code === 'NETWORK_ERROR') {
        return t(
            'upc.error.network',
            'Unable to connect. Please check your internet connection and try again.'
        );
    }
    if (error instanceof PaymentError && error.code === 'POLLING_TIMEOUT') {
        return t(
            'upc.error.pollingTimeout',
            'The payment is taking longer than expected. Please try again.'
        );
    }
    if (error instanceof PaymentError && error.code === 'PAYMENT_INCOMPLETE') {
        return t('upc.payment.failed', 'Payment could not be completed.');
    }
    return error.message;
}

function getTraceId(error: PaymentError | Error): string | undefined {
    if (error instanceof PaymentError) {
        return error.traceId;
    }
    return undefined;
}

export const UnifiedError: React.FC<UnifiedErrorProps> = ({
    message,
    severity = 'error',
    dismissible = false,
    onDismiss,
    onRetry,
    retrying = false,
    error
}) => {
    const { t } = useI18n();
    const errorMessage = error ? getErrorMessage(error, t) : null;
    const displayMessage =
        errorMessage ||
        message ||
        t('upc.error.unexpected', 'An unexpected error occurred');
    const traceId = error ? getTraceId(error) : undefined;

    const isWarning = severity === 'warning';
    const textColor = isWarning ? 'warning.main' : 'error';
    const retryColor = isWarning ? 'warning' : 'error';

    return (
        <Alert
            severity={severity}
            onClose={dismissible && onDismiss ? onDismiss : undefined}
            sx={{
                '& .MuiAlert-message': { width: '100%' },
                '& .MuiAlert-icon': { color: textColor }
            }}
        >
            <FlexBox stack gap={1} width="100%">
                <Typography color={textColor}>
                    {displayMessage}
                </Typography>
                {onRetry && (
                    <Button
                        variant="outlined"
                        color={retryColor}
                        size="small"
                        loading={retrying}
                        onClick={onRetry}
                        sx={{ alignSelf: 'flex-start' }}
                    >
                        {t('upc.button.retry', 'Try again')}
                    </Button>
                )}
                {traceId && traceId.trim() && (
                    <Typography variant="detail" color={textColor}>
                        Trace ID: {traceId}
                    </Typography>
                )}
            </FlexBox>
        </Alert>
    );
};

export const PaymentMethodError: React.FC<UnifiedErrorProps> = (props) => (
    <UnifiedError {...props} dismissible={true} />
);
